import React, { useState, useEffect, useRef } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import swooshSound from "../assets/swoosh.mp3";
import DeletableCarousel from "../components/DeletableCarousel";
import FavoritesContextMenu from "../components/FavoritesContextMenu";
import FavoritesSortPill from "../components/FavoritesSortPill";
import LeftMenu from "../components/LeftMenu";
import MobileMenu from "../components/MobileMenu";
import PlyrVideoPlayer from "../components/PlyrVideoPlayer";
import useFontSize from "../hooks/useFontSize";
import apiService from "../services/api";
import { debugLog } from "../utils/debugLog";
import { sortFavorites } from "../utils/favoritesSort";
import webPlayerStorage from "../utils/webPlayerStorage";

const MAX_ROW_ITEMS = 10;

const SORT_FROM_API = {
  name: "name",
  "recently-watched": "recently",
  "date-added": "dateAdded",
  custom: "custom",
};

const SORT_TO_API = {
  name: "name",
  recently: "recently-watched",
  dateAdded: "date-added",
  custom: "custom",
};

const EMPTY_FAVORITES_MESSAGES = [
  "Still waiting on your favs... ⏳⭐",
  "No favs? This is awkward. 😬📭",
  "So empty, it echoes! 🗣️🌀",
  "Add something! It's lonely. 🥺📺",
  "Your favs ghosted us. 👻💔",
  "Just vibes. No content. ✨😎",
  "Plot twist: nothing here. 🎬❌",
  "Give this page a purpose! 🎯📄",
  "Insert favs to continue... 🕹️⏩",
  "Empty, but full of potential! 🚀⭐",
];

const SectionLabel = ({ icon, title, count }) => (
  <h2 className="text-2xl font-bold text-white mb-6">
    <i className={`fas ${icon} mr-2 text-base`} />
    {title}{" "}
    <span className="text-gray-400 text-lg font-semibold">({count})</span>
  </h2>
);

const WebPlayerWatchlist = () => {
  useFontSize(); // Apply font size settings
  const navigate = useNavigate();
  const location = useLocation();
  const profile =
    location.state?.profile ||
    JSON.parse(sessionStorage.getItem("selectedProfile") || "{}");
  const playlist =
    location.state?.playlist ||
    JSON.parse(localStorage.getItem("selectedPlaylist") || "{}");
  const playlistId = playlist?.id || localStorage.getItem("currentPlaylistId");

  // Initialize with cached data
  const [favoritedTV, setFavoritedTV] = useState(() => {
    const cached = sessionStorage.getItem("watchlist_tv");

    return cached ? JSON.parse(cached) : [];
  });
  const [favoritedSeries, setFavoritedSeries] = useState(() => {
    const cached = sessionStorage.getItem("watchlist_series");

    return cached ? JSON.parse(cached) : [];
  });
  const [favoritedMovies, setFavoritedMovies] = useState(() => {
    const cached = sessionStorage.getItem("watchlist_movies");

    return cached ? JSON.parse(cached) : [];
  });
  const [favoritedCollections, setFavoritedCollections] = useState([]);
  const [favoritedCast, setFavoritedCast] = useState([]);
  const [emptyMessage] = useState(
    () =>
      EMPTY_FAVORITES_MESSAGES[
        Math.floor(Math.random() * EMPTY_FAVORITES_MESSAGES.length)
      ]
  );
  const [totalItems, setTotalItems] = useState(0);
  const [deletingItems, setDeletingItems] = useState({});
  const [playerOpen, setPlayerOpen] = useState(false);
  const [currentStreamUrl, setCurrentStreamUrl] = useState(null);
  const [currentStreamTitle, setCurrentStreamTitle] = useState(null);
  const [currentPlayingItem, setCurrentPlayingItem] = useState(null);
  const longPressTimers = useRef({});

  const sortRevisionRef = useRef(0);
  const [sortOption, setSortOption] = useState(
    () => localStorage.getItem("favoritesSortOption") || "name"
  );
  const [sortDirection, setSortDirection] = useState(
    () => localStorage.getItem("favoritesSortDirection") || "asc"
  );
  const [contextMenu, setContextMenu] = useState(null);
  const [moveMode, setMoveMode] = useState(null);

  useEffect(() => {
    // Check for profile and redirect if not present
    if (!profile || !profile.id) {
      navigate("/web-player/profiles");

      return;
    }

    // Check for webPlayerToken - this is what we need for API calls
    const webPlayerToken = webPlayerStorage.getToken();

    if (!webPlayerToken) {
      console.error("No webPlayerToken found, redirecting to login");
      navigate("/web-player");

      return;
    }

    // Store profile in sessionStorage for later use
    sessionStorage.setItem("selectedProfile", JSON.stringify(profile));

    // Load watchlist
    loadWatchlist();
  }, [profile, navigate, playlistId]);

  const loadWatchlist = async () => {
    try {
      // Fetch bookmarks from /v2/bookmarks
      const response = await apiService.webPlayerRequest("/v2/bookmarks");

      debugLog("Watchlist API Response:", response);

      // Log the first TV item to debug EPG data
      if (response?.data?.liveTv?.[0]) {
        debugLog("First TV item with EPG:", response.data.liveTv[0]);
      }

      // Initialize arrays for different content types
      const favTV = [];
      const favSeries = [];
      const favMovies = [];

      // Process the response data based on the actual API structure
      if (response && response.data) {
        const bookmarksData = response.data;

        // Process live TV bookmarks if they exist
        if (Array.isArray(bookmarksData)) {
          // If response.data is directly an array (live TV items)
          bookmarksData.forEach((item) => {
            // Find the current program based on current time
            const now = Date.now();
            const currentProgramme =
              item.programmes?.find(
                (prog) => prog.start <= now && prog.stop > now
              ) || item.programmes?.[0];

            const normalizedItem = {
              id: item.mediaId || item.id,
              mediaId: item.mediaId || item.id,
              title: item.name || item.title,
              name: item.name || item.title,
              posterPath: item.posterPath || item.logoPath,
              backdropPath: item.backdropPath || currentProgramme?.backdropPath,
              logoPath: item.logoPath,
              logo: item.logoPath,
              year: item.year,
              type: "live-tv",
              rating: item.rating,
              genres: item.genres,
              programmes: item.programmes,
              epgTitle: currentProgramme?.title,
              currentProgram: currentProgramme?.title,
              nowPlaying: currentProgramme?.title,
              epgDuration: currentProgramme
                ? `${Math.round((currentProgramme.stop - currentProgramme.start) / 60000)} min`
                : null,
              progress: currentProgramme // Calculate progress
                ? Math.min(
                    100,
                    Math.max(
                      0,
                      Math.round(
                        ((now - currentProgramme.start) /
                          (currentProgramme.stop - currentProgramme.start)) *
                          100
                      )
                    )
                  )
                : 0,
            };

            favTV.push(normalizedItem);
          });
        } else {
          // Handle structured response with movies, series, and potentially live sections

          // Process movies
          if (bookmarksData.movies && Array.isArray(bookmarksData.movies)) {
            debugLog(
              `Processing ${bookmarksData.movies.length} bookmarked movies`
            );
            bookmarksData.movies.forEach((item) => {
              const normalizedItem = {
                id: item.mediaId,
                mediaId: item.mediaId,
                title: item.name,
                name: item.name,
                poster: item.posterPath, // MoviesCarousel expects 'poster'
                posterPath: item.posterPath,
                backdropPath: item.backdropPath,
                logoPath: item.logoPath,
                year: item.year,
                type: "movie",
                rating: item.rating,
                genres: item.genres,
                tmdbId: item.tmdbId,
                duration: item.duration,
                dateAdded: item.dateAdded,
                description: item.description,
                rottenTomatoes: item.rottenTomatoes,
                rottenTomatoesFull: item.rottenTomatoes, // Keep full structure for display
                isBookmarked: item.isBookmarked,
              };

              favMovies.push(normalizedItem);
            });
          }

          // Process series
          if (bookmarksData.series && Array.isArray(bookmarksData.series)) {
            debugLog(
              `Processing ${bookmarksData.series.length} bookmarked series`
            );
            bookmarksData.series.forEach((item) => {
              const normalizedItem = {
                id: item.mediaId,
                mediaId: item.mediaId,
                title: item.name,
                name: item.name,
                poster: item.posterPath, // SeriesCarousel expects 'poster'
                posterPath: item.posterPath,
                backdropPath: item.backdropPath,
                logoPath: item.logoPath,
                year: item.year,
                type: "series",
                rating: item.rating,
                genres: item.genres,
                tmdbId: item.tmdbId,
                duration: item.duration,
                dateAdded: item.dateAdded,
                description: item.description,
                rottenTomatoes: item.rottenTomatoes,
                rottenTomatoesFull: item.rottenTomatoes, // Keep full structure for display
                isBookmarked: item.isBookmarked,
              };

              favSeries.push(normalizedItem);
            });
          }

          // Process live TV if it exists in the structured response
          // Check both 'live' and 'liveTv' keys
          const liveData = bookmarksData.liveTv || bookmarksData.live;

          if (liveData && Array.isArray(liveData)) {
            liveData.forEach((item) => {
              // Find the current program based on current time
              const now = Date.now();
              const currentProgramme =
                item.programmes?.find(
                  (prog) => prog.start <= now && prog.stop > now
                ) || item.programmes?.[0];

              const normalizedItem = {
                id: item.mediaId || item.id,
                mediaId: item.mediaId || item.id,
                title: item.name || item.title,
                name: item.name || item.title,
                posterPath: item.posterPath || item.logoPath,
                backdropPath:
                  item.backdropPath || currentProgramme?.backdropPath,
                logoPath: item.logoPath,
                logo: item.logoPath, // For EmblaCarousel
                year: item.year,
                type: "live-tv",
                rating: item.rating,
                genres: item.genres,
                programmes: item.programmes,
                streamUrl: item.streamUrl, // Preserve the base64 encoded stream URL
                epgTitle: currentProgramme?.title,
                currentProgram: currentProgramme?.title, // For RecentlyWatchedCarousel
                epgDuration: currentProgramme
                  ? `${Math.round((currentProgramme.stop - currentProgramme.start) / 60000)} min`
                  : null,
                duration: currentProgramme // Add duration for display
                  ? `${Math.round((currentProgramme.stop - currentProgramme.start) / 60000)} min`
                  : null,
                progress: currentProgramme // Calculate progress
                  ? Math.min(
                      100,
                      Math.max(
                        0,
                        Math.round(
                          ((now - currentProgramme.start) /
                            (currentProgramme.stop - currentProgramme.start)) *
                            100
                        )
                      )
                    )
                  : 0,
              };

              favTV.push(normalizedItem);
            });
          }
        }
      }

      // If no favorites from API, try to get from localStorage (for development)
      if (
        favTV.length === 0 &&
        favSeries.length === 0 &&
        favMovies.length === 0
      ) {
        const storedFavorites = localStorage.getItem("userFavorites");

        if (storedFavorites) {
          const parsed = JSON.parse(storedFavorites);

          if (parsed.tv) favTV.push(...parsed.tv);
          if (parsed.series) favSeries.push(...parsed.series);
          if (parsed.movies) favMovies.push(...parsed.movies);
        }
      }

      const rawBookmarks = response?.data;
      const favCollections =
        rawBookmarks &&
        !Array.isArray(rawBookmarks) &&
        Array.isArray(rawBookmarks.collections)
          ? rawBookmarks.collections.map((item) => ({
              id: item.id,
              mediaId: item.id,
              name: item.name,
              title: item.name,
              poster: item.posterPath || item.backdropPath,
              posterPath: item.posterPath,
              backdropPath: item.backdropPath,
              type: "collection",
              mediaType: "collection",
            }))
          : [];
      const favPeople =
        rawBookmarks &&
        !Array.isArray(rawBookmarks) &&
        Array.isArray(rawBookmarks.people)
          ? rawBookmarks.people.map((item) => ({
              id: item.personId || item.id,
              mediaId: item.personId || item.id,
              name: item.name,
              title: item.name,
              poster: item.profilePath,
              posterPath: item.profilePath,
              type: "cast",
              mediaType: "cast",
            }))
          : [];

      debugLog("Bookmarks loaded:", {
        tv: favTV.length,
        series: favSeries.length,
        movies: favMovies.length,
        collections: favCollections.length,
        cast: favPeople.length,
        total:
          favTV.length +
          favSeries.length +
          favMovies.length +
          favCollections.length +
          favPeople.length,
      });

      setFavoritedTV(favTV);
      setFavoritedSeries(favSeries);
      setFavoritedMovies(favMovies);
      setFavoritedCollections(favCollections);
      setFavoritedCast(favPeople);

      try {
        const preference = await apiService.webPlayerRequest(
          "/v2/bookmarks/sort-preference"
        );
        const mapped = SORT_FROM_API[preference?.data?.option];

        if (mapped) {
          const direction =
            preference.data.direction === "desc" ? "desc" : "asc";

          setSortOption(mapped);
          setSortDirection(direction);
          sortRevisionRef.current = Number(preference.data.revision) || 0;
          localStorage.setItem("favoritesSortOption", mapped);
          localStorage.setItem("favoritesSortDirection", direction);
        }
      } catch (preferenceError) {
        debugLog("Favorites sort preference unavailable:", preferenceError);
      }
      setTotalItems(
        favTV.length +
          favSeries.length +
          favMovies.length +
          favCollections.length +
          favPeople.length
      );

      // Cache the data
      sessionStorage.setItem("watchlist_tv", JSON.stringify(favTV));
      sessionStorage.setItem("watchlist_series", JSON.stringify(favSeries));
      sessionStorage.setItem("watchlist_movies", JSON.stringify(favMovies));
    } catch (error) {
      console.error("Error loading watchlist:", error);
      // Try to load from localStorage as fallback
      const storedFavorites = localStorage.getItem("userFavorites");

      if (storedFavorites) {
        try {
          const parsed = JSON.parse(storedFavorites);

          setFavoritedTV(parsed.tv || []);
          setFavoritedSeries(parsed.series || []);
          setFavoritedMovies(parsed.movies || []);
          setTotalItems(
            (parsed.tv?.length || 0) +
              (parsed.series?.length || 0) +
              (parsed.movies?.length || 0)
          );
        } catch (parseError) {
          console.error("Error parsing stored favorites:", parseError);
        }
      }
    }
  };

  const handleContentClick = (item) => {
    // Don't navigate if the item is being deleted
    if (deletingItems[item.id]) return;

    if (item.mediaType === "cast" || item.type === "cast") {
      const castId = item.mediaId || item.id;

      if (castId) {
        navigate(`/web-player/cast/${castId}`, {
          state: { playlist, profile },
        });
      }

      return;
    }

    if (item.mediaType === "collection" || item.type === "collection") {
      navigate("/web-player/movies/collections", {
        state: {
          playlist,
          profile,
          collectionId: item.mediaId || item.id,
          collectionName: item.name,
        },
      });

      return;
    }

    if (item.type === "live-tv") {
      // Handle live TV playback
      debugLog("Playing live TV:", item);

      // Build the stream URL for live TV
      let { streamUrl } = item;

      // If no streamUrl but we have stream_id, construct it using playlist data
      if (!streamUrl && item.id && playlist) {
        // Extract base URL and credentials from playlist if available
        // Format: https://domain/username/password/stream_id
        if (playlist.url && playlist.username && playlist.password) {
          const baseUrl = playlist.url.replace(/\/$/, ""); // Remove trailing slash

          streamUrl = `${baseUrl}/${playlist.username}/${playlist.password}/${item.id}`;
        }
      }

      // Decode base64 if necessary
      if (streamUrl && !streamUrl.startsWith("http")) {
        try {
          debugLog("Decoding base64 stream URL:", streamUrl);
          streamUrl = atob(streamUrl);
          debugLog("Decoded stream URL:", streamUrl);
        } catch (e) {
          console.error("Failed to decode stream URL:", e);
        }
      }

      if (streamUrl) {
        debugLog("Playing stream with URL:", streamUrl);
        // Launch the video player directly for live TV
        setCurrentPlayingItem(item);
        setPlayerOpen(true);
        setCurrentStreamUrl(streamUrl);
        setCurrentStreamTitle(item.name || item.title);
      } else {
        console.error("No stream URL available for live TV item:", item);
      }
    } else if (item.type === "movie") {
      navigate(`/web-player/movie/${item.id}`, {
        state: { playlist, profile, movie: item },
      });
    } else if (item.type === "series") {
      navigate(`/web-player/series/${item.id}`, {
        state: { playlist, profile, series: item },
      });
    }
  };

  const handleDeleteItem = async (item, type) => {
    try {
      // Play swoosh sound and show animation at the same time
      const audio = new Audio(swooshSound);

      audio.play().catch((e) => debugLog("Audio play failed:", e));

      // Mark item as being deleted (for animation)
      setDeletingItems((prev) => ({ ...prev, [item.id]: true }));

      // Wait for poof animation to play
      await new Promise((resolve) => setTimeout(resolve, 600));

      // Remove from appropriate list after animation
      if (type === "tv") {
        setFavoritedTV((prev) => {
          const updated = prev.filter((i) => i.id !== item.id);

          sessionStorage.setItem("watchlist_tv", JSON.stringify(updated));

          return updated;
        });
      } else if (type === "series") {
        setFavoritedSeries((prev) => {
          const updated = prev.filter((i) => i.id !== item.id);

          sessionStorage.setItem("watchlist_series", JSON.stringify(updated));

          return updated;
        });
      } else if (type === "movie") {
        setFavoritedMovies((prev) => {
          const updated = prev.filter((i) => i.id !== item.id);

          sessionStorage.setItem("watchlist_movies", JSON.stringify(updated));

          return updated;
        });
      }

      // Update total count
      setTotalItems((prev) => prev - 1);

      // Call API to remove bookmark
      const mediaType =
        type === "tv" ? "live-tv" : type === "movie" ? "movie" : "series";
      const mediaId = item.mediaId || item.id;

      if (!playlistId) {
        throw new Error("No playlist ID available");
      }

      // Check if we have webPlayerToken
      const webPlayerToken = webPlayerStorage.getToken();

      if (!webPlayerToken) {
        throw new Error(
          "No webPlayerToken available for API call - please login again"
        );
      }

      // Call the API to delete bookmark (matches Android: POST /v2/bookmarks/delete with { mediaId, mediaType })
      const deleteResponse = await apiService.webPlayerDeleteBookmark(
        mediaId,
        mediaType
      );

      debugLog("Delete response:", deleteResponse);

      toast.success("Removed from favorites");
    } catch (error) {
      console.error("Error removing from watchlist:", error);
      toast.error(error.message || "Failed to remove from favorites");

      // Restore item on error
      if (type === "tv") {
        setFavoritedTV((prev) => [...prev, item]);
      } else if (type === "series") {
        setFavoritedSeries((prev) => [...prev, item]);
      } else if (type === "movie") {
        setFavoritedMovies((prev) => [...prev, item]);
      }
      setTotalItems((prev) => prev + 1);
    } finally {
      // Clean up deleting state
      setDeletingItems((prev) => {
        const newState = { ...prev };

        delete newState[item.id];

        return newState;
      });
    }
  };

  const handleLongPressStart = (item, type) => {
    const timerId = setTimeout(() => {
      // Vibrate if supported
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      setContextMenu({ item, type });
    }, 500); // 500ms hold time

    longPressTimers.current[item.id] = timerId;
  };

  const handleLongPressEnd = (itemId) => {
    if (longPressTimers.current[itemId]) {
      clearTimeout(longPressTimers.current[itemId]);
      delete longPressTimers.current[itemId];
    }
  };

  const handleSortChange = (option, direction) => {
    setSortOption(option);
    setSortDirection(direction);
    localStorage.setItem("favoritesSortOption", option);
    localStorage.setItem("favoritesSortDirection", direction);

    const expectedRevision = sortRevisionRef.current || 0;

    apiService
      .webPlayerRequest("/v2/bookmarks/sort-preference", {
        method: "PATCH",
        body: JSON.stringify({
          option: SORT_TO_API[option] || option,
          direction,
          expectedRevision,
        }),
      })
      .then((response) => {
        if (response?.data?.revision != null) {
          sortRevisionRef.current = Number(response.data.revision) || 0;
        }
      })
      .catch((error) => {
        debugLog("Failed to save favorites sort preference:", error);
      });
  };

  const rowSetterFor = (type) =>
    ({
      tv: setFavoritedTV,
      series: setFavoritedSeries,
      movie: setFavoritedMovies,
      collection: setFavoritedCollections,
      cast: setFavoritedCast,
    })[type] || setFavoritedMovies;

  const rowListFor = (type) =>
    ({
      tv: favoritedTV,
      series: favoritedSeries,
      movie: favoritedMovies,
      collection: favoritedCollections,
      cast: favoritedCast,
    })[type] || favoritedMovies;

  const cacheKeyFor = (type) =>
    type === "tv"
      ? "watchlist_tv"
      : type === "series"
        ? "watchlist_series"
        : "watchlist_movies";

  const handleStartMove = (item, type) => {
    // Reorder only makes sense in the saved (custom) order, so switch to it.
    handleSortChange("custom", "asc");
    setContextMenu(null);
    setMoveMode({ id: item.id, type });
  };

  const handleMove = (direction) => {
    if (!moveMode) return;
    const { id, type } = moveMode;

    rowSetterFor(type)((prev) => {
      const arr = [...prev];
      const i = arr.findIndex((x) => x.id === id);

      if (i < 0) return prev;
      const j = direction === "left" ? i - 1 : i + 1;

      if (j < 0 || j >= arr.length) return prev;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      sessionStorage.setItem(cacheKeyFor(type), JSON.stringify(arr));

      return arr;
    });
  };

  const persistOrder = async (type, arr) => {
    const apiType =
      type === "tv" ? "live-tv" : type === "series" ? "tv" : "movie";
    const items = arr.map((it, idx) => ({
      mediaId: String(it.mediaId ?? it.id),
      sortOrder: idx,
      ...(it.tmdbId ? { tmdbId: it.tmdbId } : {}),
    }));

    try {
      await apiService.webPlayerReorderBookmarks(
        apiType,
        items,
        playlist?.userId || playlist?.id || playlistId
      );
      toast.success("Favorites order saved");
    } catch (error) {
      console.error("Reorder failed:", error);
      toast.error("Couldn't save order to server (kept locally)");
    }
  };

  const handleMoveDone = () => {
    if (!moveMode) return;
    const { type } = moveMode;

    setMoveMode(null);
    persistOrder(type, rowListFor(type));
  };

  const handleMoveToTop = (item, type) => {
    handleSortChange("custom", "asc");
    const arr = [...rowListFor(type)];
    const i = arr.findIndex((x) => x.id === item.id);

    if (i <= 0) return;
    const [moved] = arr.splice(i, 1);

    arr.unshift(moved);
    rowSetterFor(type)(arr);
    sessionStorage.setItem(cacheKeyFor(type), JSON.stringify(arr));
    persistOrder(type, arr);
  };

  const goToGrid = (type) => {
    navigate(`/web-player/favorites/${type}`, {
      state: {
        profile,
        playlist,
        type,
        items: rowListFor(type),
        sortOption,
        sortDirection,
      },
    });
  };

  // No need for custom LiveTVRow, we'll use ContentRow directly

  return (
    <div className="min-h-screen">
      {/* Dashboard Button - Bottom Right */}
      <button
        className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-50 text-white text-xs sm:text-sm font-medium bg-black/50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg opacity-40 hover:opacity-100 hover:scale-105 transition-all duration-300"
        onClick={() => {
          // Clear all session data
          webPlayerStorage.clearAll();
          // Redirect to dashboard
          window.location.href = "https://dashboard.lunova.app";
        }}
      >
        Dashboard
      </button>

      {/* Left Menu */}
      <LeftMenu
        activeMenuItem="watchlist"
        playlist={playlist}
        profile={profile}
      />

      {/* Mobile Menu */}
      <MobileMenu />

      {/* Main Content */}
      <div className="min-w-0 pb-12">
        {/* Header */}
        <div className="flex items-center justify-between pb-8 pl-20 pr-4 pt-12 lg:pl-[calc(4rem+30px)] lg:pr-8 xl:pl-[calc(5rem+30px)]">
          <h1 className="text-3xl font-bold text-white">
            Favorites
            <span className="text-lg text-gray-400 ml-2 align-super">
              ({totalItems})
            </span>
          </h1>
          {totalItems > 0 && (
            <FavoritesSortPill
              direction={sortDirection}
              option={sortOption}
              onChange={handleSortChange}
            />
          )}
        </div>

        {/* Content Sections */}
        <div className="pl-20 pr-4 lg:pl-[calc(4rem+30px)] lg:pr-8 xl:pl-[calc(5rem+30px)]">
          {/* Favorited TV Section */}
          {favoritedTV.length > 0 && (
            <div className="mb-8">
              <SectionLabel
                count={favoritedTV.length}
                icon="fa-bolt"
                title="Favorited TV"
              />
              <div
                className="pr-8 pb-4"
                style={{
                  overflow: "visible",
                  filter: "drop-shadow(0 25px 25px rgba(0, 0, 0, 0.15))",
                }}
              >
                <DeletableCarousel
                  deletingItems={deletingItems}
                  items={sortFavorites(
                    favoritedTV,
                    sortOption,
                    sortDirection
                  ).slice(0, MAX_ROW_ITEMS)}
                  itemsVisible={5.5}
                  moveModeId={moveMode?.type === "tv" ? moveMode.id : null}
                  seeAllCount={
                    favoritedTV.length > MAX_ROW_ITEMS ? favoritedTV.length : 0
                  }
                  type="logo"
                  onItemClick={handleContentClick}
                  onLongPressEnd={handleLongPressEnd}
                  onLongPressStart={(item) => handleLongPressStart(item, "tv")}
                  onMoveDone={handleMoveDone}
                  onMoveLeft={() => handleMove("left")}
                  onMoveRight={() => handleMove("right")}
                  onSeeAll={() => goToGrid("tv")}
                />
              </div>
            </div>
          )}

          {/* Favorited Series Section */}
          {favoritedSeries.length > 0 && (
            <div className="mb-8">
              <SectionLabel
                count={favoritedSeries.length}
                icon="fa-tv"
                title="Favorited Series"
              />
              <div
                className="pr-8 pb-4"
                style={{
                  overflow: "visible",
                  filter: "drop-shadow(0 25px 25px rgba(0, 0, 0, 0.15))",
                }}
              >
                <DeletableCarousel
                  deletingItems={deletingItems}
                  items={sortFavorites(
                    favoritedSeries,
                    sortOption,
                    sortDirection
                  ).slice(0, MAX_ROW_ITEMS)}
                  itemsVisible={5.5}
                  moveModeId={moveMode?.type === "series" ? moveMode.id : null}
                  seeAllCount={
                    favoritedSeries.length > MAX_ROW_ITEMS
                      ? favoritedSeries.length
                      : 0
                  }
                  type="poster"
                  onItemClick={handleContentClick}
                  onLongPressEnd={handleLongPressEnd}
                  onLongPressStart={(item) =>
                    handleLongPressStart(item, "series")
                  }
                  onMoveDone={handleMoveDone}
                  onMoveLeft={() => handleMove("left")}
                  onMoveRight={() => handleMove("right")}
                  onSeeAll={() => goToGrid("series")}
                />
              </div>
            </div>
          )}

          {/* Favorited Movies Section */}
          {favoritedMovies.length > 0 && (
            <div className="mb-8">
              <SectionLabel
                count={favoritedMovies.length}
                icon="fa-film"
                title="Favorited Movies"
              />
              <div
                className="pr-8 pb-4"
                style={{
                  overflow: "visible",
                  filter: "drop-shadow(0 25px 25px rgba(0, 0, 0, 0.15))",
                }}
              >
                <DeletableCarousel
                  deletingItems={deletingItems}
                  items={sortFavorites(
                    favoritedMovies,
                    sortOption,
                    sortDirection
                  ).slice(0, MAX_ROW_ITEMS)}
                  itemsVisible={5.5}
                  moveModeId={moveMode?.type === "movie" ? moveMode.id : null}
                  seeAllCount={
                    favoritedMovies.length > MAX_ROW_ITEMS
                      ? favoritedMovies.length
                      : 0
                  }
                  type="poster"
                  onItemClick={handleContentClick}
                  onLongPressEnd={handleLongPressEnd}
                  onLongPressStart={(item) =>
                    handleLongPressStart(item, "movie")
                  }
                  onMoveDone={handleMoveDone}
                  onMoveLeft={() => handleMove("left")}
                  onMoveRight={() => handleMove("right")}
                  onSeeAll={() => goToGrid("movie")}
                />
              </div>
            </div>
          )}

          {favoritedCollections.length > 0 && (
            <div className="mb-8">
              <SectionLabel
                count={favoritedCollections.length}
                icon="fa-bolt"
                title="Saved Collections"
              />
              <div className="pr-8 pb-4">
                <DeletableCarousel
                  deletingItems={deletingItems}
                  items={sortFavorites(
                    favoritedCollections,
                    sortOption,
                    sortDirection
                  ).slice(0, MAX_ROW_ITEMS)}
                  itemsVisible={5.5}
                  seeAllCount={
                    favoritedCollections.length > MAX_ROW_ITEMS
                      ? favoritedCollections.length
                      : 0
                  }
                  type="poster"
                  onItemClick={handleContentClick}
                  onSeeAll={() => goToGrid("collection")}
                />
              </div>
            </div>
          )}

          {favoritedCast.length > 0 && (
            <div className="mb-8">
              <SectionLabel
                count={favoritedCast.length}
                icon="fa-users"
                title="Favorited Cast"
              />
              <div className="pr-8 pb-4">
                <DeletableCarousel
                  deletingItems={deletingItems}
                  items={sortFavorites(
                    favoritedCast,
                    sortOption,
                    sortDirection
                  ).slice(0, MAX_ROW_ITEMS)}
                  itemsVisible={5.5}
                  seeAllCount={
                    favoritedCast.length > MAX_ROW_ITEMS
                      ? favoritedCast.length
                      : 0
                  }
                  type="poster"
                  onItemClick={handleContentClick}
                  onSeeAll={() => goToGrid("cast")}
                />
              </div>
            </div>
          )}

          {/* Empty State */}
          {favoritedTV.length === 0 &&
            favoritedSeries.length === 0 &&
            favoritedMovies.length === 0 &&
            favoritedCollections.length === 0 &&
            favoritedCast.length === 0 && (
              <div className="text-center py-20">
                <i className="fas fa-star text-6xl text-gray-600 mb-4" />
                <p className="text-gray-400 text-xl">{emptyMessage}</p>
              </div>
            )}
        </div>
      </div>

      {/* Long-press context menu */}
      {contextMenu && (
        <FavoritesContextMenu
          item={contextMenu.item}
          type={contextMenu.type}
          onClose={() => setContextMenu(null)}
          onMove={() => handleStartMove(contextMenu.item, contextMenu.type)}
          onMoveToTop={() =>
            handleMoveToTop(contextMenu.item, contextMenu.type)
          }
          onPlay={() => handleContentClick(contextMenu.item)}
          onRemove={() => handleDeleteItem(contextMenu.item, contextMenu.type)}
        />
      )}

      {/* Move-mode hint */}
      {moveMode && (
        <div className="fixed left-1/2 top-4 z-[120] -translate-x-1/2 rounded-full bg-blue-600 px-4 py-2 text-sm text-white shadow-lg">
          Use the arrows to reorder, then tap ✓
        </div>
      )}

      {/* Video Player Modal */}
      {playerOpen && (
        <PlyrVideoPlayer
          episodeId={(() => {
            // For series, extract episodeId from compound mediaId (seriesId-episodeId)
            const mediaType =
              currentPlayingItem?.type === "live" ||
              currentPlayingItem?.type === "livetv"
                ? "livetv"
                : currentPlayingItem?.type || "movie";

            if (
              mediaType === "series" ||
              mediaType === "tv" ||
              mediaType === "episode"
            ) {
              const mediaId = String(
                currentPlayingItem?.mediaId || currentPlayingItem?.id || ""
              );

              if (mediaId.includes("-")) {
                return mediaId.split("-")[1];
              }
            }

            return undefined;
          })()}
          isOpen={playerOpen}
          mediaId={currentPlayingItem?.mediaId || currentPlayingItem?.id}
          mediaType={
            currentPlayingItem?.type === "live" ||
            currentPlayingItem?.type === "livetv"
              ? "livetv"
              : currentPlayingItem?.type || "movie"
          }
          seriesId={(() => {
            // For series, extract seriesId from compound mediaId (seriesId-episodeId)
            const mediaType =
              currentPlayingItem?.type === "live" ||
              currentPlayingItem?.type === "livetv"
                ? "livetv"
                : currentPlayingItem?.type || "movie";

            if (
              mediaType === "series" ||
              mediaType === "tv" ||
              mediaType === "episode"
            ) {
              const mediaId = String(
                currentPlayingItem?.mediaId || currentPlayingItem?.id || ""
              );

              if (mediaId.includes("-")) {
                return mediaId.split("-")[0];
              }

              return mediaId;
            }

            return undefined;
          })()}
          streamUrl={currentStreamUrl}
          title={currentStreamTitle}
          onClose={() => {
            setPlayerOpen(false);
            setCurrentStreamUrl(null);
            setCurrentStreamTitle(null);
            setCurrentPlayingItem(null);
          }}
        />
      )}
    </div>
  );
};

export default WebPlayerWatchlist;
